import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { Contact, ContactDetails } from "../../../../../api/types/fundStructureTypes";
import type { FieldIdToValueMap, ObjectClassDefinition } from "../../../../../api/types/objectTypes";
import type { EmailFieldEditMode } from "../../../../entityFields/FieldValuesContext";
import FieldValuesSection from "../../../../entityFields/FieldValuesSection";
import { checkContactEmailAvailability, emailAvailabilityUnknownText } from "./contactEmailAvailability";
import ContactEmailChangeDialog from "./ContactEmailChangeDialog";
import type { ContactEmailEditMode } from "./contactEmailEditModel";
import EmailInUseConfirmationDialog from "./EmailInUseConfirmationDialog";
import PortalAccessContext from "./portal-access/PortalAccessContext";

interface Props {
  contact: Contact;
  objectDefinition: ObjectClassDefinition;
  onContactDetailsChange: (details: Partial<ContactDetails>) => void;
  onUpdateContactDetails: () => void;
  hasPermissionsToManageFields: boolean;
}

const EMAIL_FIELD_ID = "Email";

const updateFieldValues = withErrorHandling(adminApi.updateContactFieldValues);

const getEmailFieldEditMode = (mode: ContactEmailEditMode, onEdit: () => void): EmailFieldEditMode | undefined => {
  switch (mode.kind) {
    case "dialog":
      return { fieldId: EMAIL_FIELD_ID, kind: "dialog", onEdit };
    case "disabled":
      return { fieldId: EMAIL_FIELD_ID, kind: "disabled", reason: mode.reason };
    case "inline":
      return undefined;
  }
};

const ContactCard = ({
  contact,
  objectDefinition,
  onContactDetailsChange,
  onUpdateContactDetails,
  hasPermissionsToManageFields,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { emailEditMode } = useDefinedContext(PortalAccessContext, "PortalAccessContext");

  const [emailChangeDialogOpen, setEmailChangeDialogOpen] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const [pendingEmailSave, setPendingEmailSave] = useState<FieldIdToValueMap>();

  const getFieldValues = useCallback(() => adminApi.getContactFieldValues(contact.id), [contact.id]);

  const [fieldValuesResp, fieldValuesError, { setData: setFieldValues, isFetching: isFetchingFieldValues, fetch }] =
    useFetch(getFieldValues, { onSetData: () => setRenderKey((prev) => prev + 1) });

  const getCardLayout = useCallback(
    () => adminApi.getCardLayout(objectDefinition.objectType),
    [objectDefinition.objectType]
  );

  const [cardLayoutResp, cardLayoutError, { isFetching: isFetchingCardLayout }] = useFetch(getCardLayout, {
    onSetData: () => setRenderKey((prev) => prev + 1),
  });

  const persistFieldValues = async (fieldValues: FieldIdToValueMap): Promise<boolean> => {
    const values = Object.entries(fieldValues).map(([fieldId, value]) => ({ fieldId, value }));
    const [resp, error] = await updateFieldValues(contact.id, { values });
    if (error) {
      sendNotificationError(error.message);
      logError(error, `[ContactDetailsFields] saveFieldValues`);
      return false;
    }

    sendNotification("Changes saved");
    setFieldValues(resp);
    setRenderKey((prev) => prev + 1);

    const nameFieldValue = resp.values.find(({ fieldId }) => fieldId.toLowerCase() === "name");
    const name = nameFieldValue?.value?.toString() || contact.name;
    onContactDetailsChange({
      contact: { ...contact, name },
      updateAt: new Date().toISOString(),
    });

    return true;
  };

  const saveFieldValues = async (fieldValues: FieldIdToValueMap): Promise<boolean> => {
    const newEmail = fieldValues[EMAIL_FIELD_ID];
    if (typeof newEmail !== "string" || !newEmail.trim()) {
      return persistFieldValues(fieldValues);
    }

    const ownership = await checkContactEmailAvailability(contact.id, newEmail, "[ContactCard]");
    if (ownership === "Unknown") {
      sendNotificationError(emailAvailabilityUnknownText);
      return false;
    }

    if (ownership === "Free") {
      return persistFieldValues(fieldValues);
    }

    setPendingEmailSave(fieldValues);
    return false;
  };

  const handleConfirmPendingSave = () => {
    if (!pendingEmailSave) {
      return;
    }

    setPendingEmailSave(undefined);
    persistFieldValues(pendingEmailSave);
  };

  if (fieldValuesError || cardLayoutError) {
    logError(fieldValuesError || cardLayoutError, "[ContactDetailsFields]");
    return <DataLoadingFailed title="Loading contact fields" />;
  }

  const handleEmailAndNameUpdate = () => {
    fetch();
    onUpdateContactDetails();
  };

  const fieldValues = fieldValuesResp
    ? Object.fromEntries(fieldValuesResp.values.map(({ fieldId, value }) => [fieldId, value]))
    : {};

  const cardLayout = cardLayoutResp ?? { objectType: objectDefinition.objectType, fieldGroups: [] };

  const emailFieldEditMode = getEmailFieldEditMode(emailEditMode, () => setEmailChangeDialogOpen(true));

  return (
    <>
      <FieldValuesSection
        key={renderKey.toString()}
        objectType={objectDefinition.objectType}
        objectId={contact.id}
        businessCentralType={objectDefinition.businessCentralType}
        fields={objectDefinition.fields}
        cardLayout={cardLayout}
        fieldValues={fieldValues}
        lookupObjects={fieldValuesResp?.lookupObjects}
        allowEditing={hasPermissionsToManageFields}
        saveFieldValues={saveFieldValues}
        emailFieldEditMode={emailFieldEditMode}
        isLoading={isFetchingFieldValues || !fieldValuesResp || isFetchingCardLayout || !cardLayoutResp}
      />

      {emailChangeDialogOpen && emailEditMode.kind === "dialog" && (
        <ContactEmailChangeDialog
          contact={contact}
          plan={emailEditMode.plan}
          onClose={() => setEmailChangeDialogOpen(false)}
          onSaved={handleEmailAndNameUpdate}
        />
      )}

      {pendingEmailSave && (
        <EmailInUseConfirmationDialog
          onConfirm={handleConfirmPendingSave}
          onClose={() => setPendingEmailSave(undefined)}
        />
      )}
    </>
  );
};

export default ContactCard;
